import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { trackAddToCart } from "@/utils/analytics";

// Cart schema version - increment when cart structure changes
const CART_SCHEMA_VERSION = 2;
const CART_STORAGE_KEY = "samboards_cart_v2";
const CART_VERSION_KEY = "samboards_cart_version";

export type CartItem = {
  id: string;
  name: string;
  slug: string;
  price: number;
  quantity: number;
  image?: string; // Product thumbnail URL
};

type CartContextType = {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "quantity">) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

// Validate cart item structure
function isValidCartItem(item: unknown): item is CartItem {
  if (!item || typeof item !== 'object') return false;
  const obj = item as Record<string, unknown>;
  return (
    typeof obj.id === 'string' &&
    obj.id.length > 0 &&
    typeof obj.name === 'string' &&
    obj.name.length > 0 &&
    typeof obj.slug === 'string' &&
    typeof obj.price === 'number' &&
    obj.price >= 0 &&
    typeof obj.quantity === 'number' &&
    obj.quantity > 0
  );
}

// Load and validate cart from localStorage
function loadCart(): CartItem[] {
  try {
    // Check schema version first
    const storedVersion = localStorage.getItem(CART_VERSION_KEY);
    const currentVersion = storedVersion ? parseInt(storedVersion, 10) : 0;
    
    if (currentVersion !== CART_SCHEMA_VERSION) {
      // Clear stale cart data from old schema
      console.log('[Cart] Schema version mismatch, clearing stale cart');
      localStorage.removeItem(CART_STORAGE_KEY);
      localStorage.removeItem("cart"); // Clear old key too
      localStorage.setItem(CART_VERSION_KEY, String(CART_SCHEMA_VERSION));
      return [];
    }
    
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (!stored) return [];
    
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    
    // Validate each item and filter out invalid ones
    const validItems = parsed.filter((item): item is CartItem => {
      const isValid = isValidCartItem(item);
      if (!isValid) {
        console.warn('[Cart] Removed invalid cart item:', item);
      }
      return isValid;
    });
    
    // If we removed items, save the cleaned cart
    if (validItems.length !== parsed.length) {
      console.log('[Cart] Cleaned cart: removed', parsed.length - validItems.length, 'invalid items');
    }
    
    return validItems;
  } catch (error) {
    console.error('[Cart] Failed to load cart:', error);
    return [];
  }
}

// Save cart to localStorage
function saveCart(items: CartItem[]): void {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    localStorage.setItem(CART_VERSION_KEY, String(CART_SCHEMA_VERSION));
  } catch (error) {
    console.error('[Cart] Failed to save cart:', error);
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => loadCart());

  // Save cart whenever items change
  useEffect(() => {
    saveCart(items);
  }, [items]);

  // Clean up old localStorage key on mount
  useEffect(() => {
    const oldCart = localStorage.getItem("cart");
    if (oldCart) {
      console.log('[Cart] Migrating from old cart key');
      localStorage.removeItem("cart");
    }
  }, []);

  const addItem = (item: Omit<CartItem, "quantity">) => {
    // Validate the item before adding
    if (!item.id || !item.name || typeof item.price !== 'number') {
      console.error('[Cart] Attempted to add invalid item:', item);
      return;
    }

    setItems((current) => {
      const existingItem = current.find((i) => i.id === item.id);
      if (existingItem) {
        return current.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...current, { ...item, quantity: 1 }];
    });
    
    // Track add to cart event
    trackAddToCart(item.id, item.name, item.price, 1);
  };

  const removeItem = (id: string) => {
    setItems((current) => current.filter((item) => item.id !== id));
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(id);
      return;
    }
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem("cart"); // Also clear old key
    localStorage.removeItem("last_order"); // Clear last order data
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
}
